import {EventEmitter, Injectable} from '@angular/core';
import {
  AdditionalCoveragesFilterOption,
  AssistanceConditionsFilterOption,
  CoveragesFilterOption,
  FilterKey,
  FilterMessage,
  FilterMessageEvent,
  FilterOffersCountState,
  FilterOption,
  FiltersApplicationMode,
  FiltersState,
  FilterType,
  NextAccidentReplacementCarFilterOption,
  NextNNWValueFilterOption,
  NextTerritorialScopeFilterOption,
  NextTowingLimitFilterOption,
  NNWValueFilterOption,
  OffersFilter,
  OwnContributionFilterOption,
  PartsDepreciationFilterOption,
  RepairPartsFilterOption
} from '../sections/web/offer-section/offers-filters/offers-filter/offers-filter.model';
import {Offer} from '../../core/models/offer/offer.model';
import {ApplicationStateService} from './application-state.service';
import {BusinessParamName} from '../models/request-offer/request-offer.enum';
import {LanguageService} from './language.service';
import {TranslateService} from '@ngx-translate/core';
import {isNext, isMobile, noop, salesChannel} from '../../core/utils/utils';
import * as _ from 'lodash';
import {SalesChannel} from '../../core/consts/sales-channel.enum';
import {UrlParamsService} from '../../core/services/impl/url-params.service';
import {OfferCoverageCode} from '../../core/consts/offer-coverages/offer-coverage-code.enum';
import {AssistanceTerritorialScopeOption} from '../../core/consts/offer-coverages/assistance-territorial-scope-option.enum';
import {AssistanceAccidentReplacementCarOption} from '../../core/consts/offer-coverages/assistance-accident-replacement-car-option.enum';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {InsuranceCoverageModel} from '../../core/models/insurance-coverage.model';
import {OfferCoveragesService} from '../../core/services/impl/offer-coverages.service';
import {OfferPrimaryCoverageCode} from '../../core/consts/offer-primary-coverage-code.enum';
import {PrimaryCoverageFilterCheckEvent} from '../sections/web/insurance-coverages-section/insurance-coverages-section.component';
import {OfferDestinyOptionType} from '../../core/enums/offer/offer-destiny-option-type.enum';

@Injectable({
  providedIn: 'root'
})
export class FiltersService {

  unconfirmedFiltersApplied: boolean;
  filtersApplied: boolean;
  filtersChangeEvent: EventEmitter<any> = new EventEmitter();
  detailCoverageFilterClick: EventEmitter<DetailCoverageFilterClickEvent> = new EventEmitter<DetailCoverageFilterClickEvent>();
  resetFiltersEvent: EventEmitter<any> = new EventEmitter();
  offersCountUpdateEvent: EventEmitter<any> = new EventEmitter();
  filterMessageEvent: EventEmitter<FilterMessageEvent> = new EventEmitter();
  acConcernedFilters: FilterKey[] = [ FilterKey.OWN_CONTRIBUTION, FilterKey.PARTS_DEPRECIATION, FilterKey.REPAIR_PARTS ];
  ocAcSelectedByUser = false;
  isNext = isNext();
  private mode: FiltersApplicationMode = FiltersApplicationMode.AUTOMATIC;
  private filters: FiltersState; // filters accepted by user (mobile) or applied immediately (desktop)
  private unconfirmedFilters: FiltersState; // object to store filters selected by user in mobile mode before acceptance
  private expanded = true;
  private offersCountState: FilterOffersCountState[] = [];
  private primaryCoverageFiltersConfig: PrimaryCoverageFilterConfig[] = [
    {
      primaryCoverageCode: OfferPrimaryCoverageCode.NNW,
      relatedDetailCoverageFilterKeys: [
          // FilterKey.NNW_RANGE
      ]
    },
    {
      primaryCoverageCode: OfferPrimaryCoverageCode.ASSISTANCE,
      relatedDetailCoverageFilterKeys: [
        // FilterKey.TOWING_LIMIT,
        // FilterKey.TERRITORIAL_SCOPE,
        // FilterKey.ACCIDENT_REPLACEMENT_CAR
      ]
    }
  ];

  get primaryCoveragesWithRelatedFilters(): OfferPrimaryCoverageCode[] {
    return this.primaryCoverageFiltersConfig.map(config => config.primaryCoverageCode);
  }

  get filtersVisible(): boolean {
    return salesChannel() === SalesChannel.KIOSKPOLIS || salesChannel() === SalesChannel.BANKIER || salesChannel() === SalesChannel.OLX ||
        salesChannel() === SalesChannel.MPAY || salesChannel() === SalesChannel.SKYCASH || (salesChannel() === SalesChannel.YANOSIK && !this.urlParamsService.isMobileOrigin);
  }

  get filtersExpanded(): boolean {
    return this.expanded && !isMobile();
  }

  get confirmedFilters(): FiltersState {
    return this.filters;
  }

  get filtersState(): FiltersState {
    return this.mode === FiltersApplicationMode.AUTOMATIC ? this.filters : this.unconfirmedFilters;
  }

  get anyFiltersSelected(): boolean {
    return this.mode === FiltersApplicationMode.AUTOMATIC ? this.filtersApplied : this.unconfirmedFiltersApplied;
  }

  get acSelected(): boolean {
    return this.filters ? this.isFilterOptionActive(FilterKey.PRIMARY_COVERAGE, 'AC') : false;
  }

  get ocOnlySelected(): boolean {
    if (this.filters && this.filters[FilterKey.PRIMARY_COVERAGE]) {
      return this.filters[FilterKey.PRIMARY_COVERAGE].length === 1 && this.filters[FilterKey.PRIMARY_COVERAGE].includes(OfferPrimaryCoverageCode.OC);
    }
    return false;
  }

  constructor(private stateService: ApplicationStateService,
              private ts: TranslateService,
              private offerCoveragesService: OfferCoveragesService,
              private urlParamsService: UrlParamsService,
              private lang: LanguageService) {
  }

  // adds selected option to filters' state & immediately update offers
  addFilter(filter: OffersFilter, optionKey: string): void {
    if (this.getOption(filter, optionKey).isAnyOption) {
      this.filtersState[filter.key] = [];
    } else {
      if (this.isCheckbox(filter)) {
        this.filtersState[filter.key].includes(optionKey) ? noop() : this.filtersState[filter.key].push(optionKey);
      } else {
        this.filtersState[filter.key] = [ optionKey ];
      }
    }
    if (this.mode === FiltersApplicationMode.AUTOMATIC) {
      this.updateOffers();
    } else {
      this.updateUnconfirmedFiltersApplied();
    }
  }

  // removes unchecked option from filters' state & immediately updates offers
  removeFilter(filter: OffersFilter, optionKey: string): void {
    this.filtersState[filter.key] = this.filtersState[filter.key].filter(option => option !== optionKey);
    if (this.mode === FiltersApplicationMode.AUTOMATIC) {
      this.updateOffers();
    } else {
      this.updateUnconfirmedFiltersApplied();
    }
    if (this.acConcernedFilters.includes(filter.key)) {
      this.checkAcConcernedFilters();
    }
  }

  // performs offers update based on current filters state (used in mobile filters)
  confirmMobileFilters(): void {
    this.filters = this.unconfirmedFilters;
    this.updateOffers();
  }

  checkOfferWithFilters(offer: Offer): boolean {
    return this.isNext ? this.testNextOffer(offer) : this.testOffer(offer);
  }

  getOption(filter: OffersFilter, optionKey: string): FilterOption {
    return filter.options.find(option => option.key === optionKey);
  }

  resetFilters(): void {
    if (this.mode === FiltersApplicationMode.AUTOMATIC) {
      this.initFilters();
      this.updateOffers();
    } else { // on confirmation mode
      this.unconfirmedFilters = this.createClearFiltersState();
      this.unconfirmedFiltersApplied = false;
    }
    this.resetFiltersEvent.emit();
  }

  toggleFiltersVisibility() {
    this.expanded = !this.expanded;
  }

  changeMode(mode: FiltersApplicationMode): void {
    this.mode = mode;
    if (this.mode === FiltersApplicationMode.ON_CONFIRMATION) {
      this.unconfirmedFilters = _.cloneDeep(this.filters);
      this.updateUnconfirmedFiltersApplied();
    }
  }

  initFilters(): void {
    this.filters = this.createClearFiltersState();
    this.unconfirmedFilters = this.createClearFiltersState();
    this.filtersApplied = false;
  }

  checkAtLeastOneAcOffer(offers: Offer[]): boolean {
    return !!offers.find(offer => {
      return this.offerCoveragesService.hasOfferPrimaryCoverage(offer, OfferPrimaryCoverageCode.AC) || this.offerCoveragesService.hasOfferPrimaryCoverage(offer, OfferPrimaryCoverageCode.AC_MINI)
    });
  }

  filterOffers(offers: Offer[]): Offer[] {
    const filteredOffers = this.filtersApplied ? offers.filter(offer => this.checkOfferWithFilters(offer)) : offers;
    if (this.ocOnlySelected) {
      return filteredOffers.filter(offer => {
        const icCode = this.stateService.getBusinessParamFromOffer(offer.id, BusinessParamName.INSURANCE_COMPANY_CODE);
        const cheapestOffer = this.offerCoveragesService.getCheapestOfferForInsuranceCompany(filteredOffers, icCode);
        return offer.id === cheapestOffer.id;
      });
    }
    return filteredOffers;
  }

  updateOffersCountState(filteredOffers: Offer[]): void {
    if (isNotNullOrUndefined(this.offersCountState)) {
      this.offersCountState.forEach(filterState => {
        filterState.optionsOffersCounts.forEach(optionState => {
          const correctOffers = filteredOffers.filter(offer => this.testOfferForFilterOption(offer, filterState.key, optionState.optionKey));
          optionState.offersCount = correctOffers.length;
        });
      });
      this.offersCountUpdateEvent.emit();
    }
  }

  createOffersCountState(filters: OffersFilter[]): void {
    this.offersCountState = filters.map(filter => ({
      key: filter.key,
      optionsOffersCounts: filter.options.map(option => ({
        optionKey: option.key,
        offersCount: this.getOfferCount(filter.key, option.key)
      }))
    }));
  }

  getOfferCount(filterKey: FilterKey, optionKey: string): number {
    const filterState = this.offersCountState.find(filter => filter.key === filterKey);
    if (isNotNullOrUndefined(filterState)) {
      const option = filterState.optionsOffersCounts.find(filterOption => filterOption.optionKey === optionKey);
      return option ? option.offersCount : 0;
    }
    return 0;
  }

  updatePrimaryCoverageFilterState(coverages: InsuranceCoverageModel): void {
    const filterState = [];
    Object.keys(coverages).forEach(key => {
      if (coverages[key]) {
        filterState.push(key);
      }
    });
    this.filtersState[FilterKey.PRIMARY_COVERAGE] = filterState;
    this.updateOffers();
  }

  anyRelatedDetailCoverageFiltersActive(primaryCoverageCode: string): boolean {
    const config = this.primaryCoverageFiltersConfig.find(item => item.primaryCoverageCode === primaryCoverageCode);
    if (config) {
      return config.relatedDetailCoverageFilterKeys
          .map(filterKey => this.filtersState[filterKey])
          .some(filterState => filterState && filterState.length > 0);
    }
    return false;
  }

  onCoverageCheckedUpdate(event: PrimaryCoverageFilterCheckEvent): void {
    if (this.primaryCoveragesWithRelatedFilters.includes(event.coverageCode)) {
      const message = event.checked ? FilterMessage.SELECT_ALL_FILTER_OPTIONS : FilterMessage.RESET_ALL_FILTER_OPTIONS;
      this.filterMessageEvent.emit({
        message: message,
        targets: <FilterKey[]>this.getPrimaryCoverageFilterRelatedFilters(event.coverageCode)
      })
    }
  }

  getPrimaryCoveragesWithRelatedFilter(filterKey: FilterKey): OfferPrimaryCoverageCode[] {
    return this.primaryCoverageFiltersConfig
        .filter(config => config.relatedDetailCoverageFilterKeys.includes(filterKey))
        .map(item => item.primaryCoverageCode);
  }

  private testOffer(offer: Offer): boolean {
    if (!this.filterByCoverages(offer)) {
      return false;
    }
    if (!this.filterByAdditionalCoverages(offer)) {
      return false;
    }
    if (!this.filterByOwnContribution(offer)) {
      return false;
    }
    if (!this.filterByRepairParts(offer)) {
      return false;
    }
    if (!this.filterByPartsDepreciation(offer)) {
      return false;
    }
    if (!this.filterByAssistanceConditions(offer)) {
      return false;
    }
    if (!this.filterByNNWValue(offer)) {
      return false;
    }
    if (!this.filterByInsuranceCompanies(offer)) {
      return false;
    }
    return true;
  }

  private testNextOffer(offer: Offer): boolean {
    if (!this.filterByPrimaryCoverage(offer)) {
      return false;
    }
    if (!this.filterByNNWRange(offer)) {
      return false;
    }
    if (!this.filterByTowingLimit(offer)) {
      return false;
    }
    if (!this.filterByTerritorialScope(offer)) {
      return false;
    }
    if (!this.filterByAccidentReplacementCar(offer)) {
      return false;
    }
    if (!this.filterByInsuranceCompanies(offer)) {
      return false;
    }
    return true;
  }

  private testOfferForFilterOption(offer: Offer, filterKey: FilterKey, optionKey: string): boolean {
    // if (this.filters[filterKey] && !!this.filters[filterKey].length && !this.filters[filterKey].includes(optionKey)) {
    //   return false;
    // }
    const filterState = [
        // ...this.filters[filterKey],
        optionKey
    ];
    switch (filterKey) {
      case FilterKey.NNW_RANGE:
        return this.filterByNNWRange(offer, filterState);
      case FilterKey.TOWING_LIMIT:
        return this.filterByTowingLimit(offer, filterState);
      case FilterKey.TERRITORIAL_SCOPE:
        return this.filterByTerritorialScope(offer, filterState);
      case FilterKey.ACCIDENT_REPLACEMENT_CAR:
        return this.filterByAccidentReplacementCar(offer, filterState);
      case FilterKey.INSURANCE_COMPANY:
        return this.filterByInsuranceCompanies(offer, filterState);
    }
  }

  private filterByCoverages(offer: Offer): boolean {
    if (!this.filters[FilterKey.COVERAGE].length) {
      return true;
    }
    const ac = this.getCoverageValue(OfferCoverageCode.AC, offer);
    const acMini = this.getCoverageValue(OfferCoverageCode.AC_MINI, offer);

    if (this.exactFiltersOptionsApplied(FilterKey.COVERAGE, [CoveragesFilterOption.OC])) { // OC
      return ac.selectedOption === 'AC_NO' && (!acMini || acMini.selectedOption === 'AC_MINI_NO');
    }
    if (this.exactFiltersOptionsApplied(FilterKey.COVERAGE, [CoveragesFilterOption.OC, CoveragesFilterOption.OC_AC])) { // OC, OC + AC
      return ac.selectedOption === 'AC_YES' || (!acMini || acMini.selectedOption === 'AC_MINI_NO');
    }
    if (this.exactFiltersOptionsApplied(FilterKey.COVERAGE, [CoveragesFilterOption.OC, CoveragesFilterOption.OC_AC_MINI])) { // OC, OC + AC Mini
      return ac.selectedOption === 'AC_NO' || (acMini && acMini.selectedOption === 'AC_MINI_YES');
    }
    if (this.exactFiltersOptionsApplied(FilterKey.COVERAGE, [CoveragesFilterOption.OC, CoveragesFilterOption.OC_AC, CoveragesFilterOption.OC_AC_MINI])) { // OC, OC + AC, OC + AC Mini
      return true;
    }
    if (this.exactFiltersOptionsApplied(FilterKey.COVERAGE, [CoveragesFilterOption.OC_AC, CoveragesFilterOption.OC_AC_MINI])) { // OC + AC, OC + AC Mini
      return ac.selectedOption === 'AC_YES' || (acMini && acMini.selectedOption === 'AC_MINI_YES');
    }
    if (this.exactFiltersOptionsApplied(FilterKey.COVERAGE, [CoveragesFilterOption.OC_AC])) { // OC + AC
      return ac.selectedOption === 'AC_YES' && (!acMini || acMini.selectedOption === 'AC_MINI_NO');
    }
    if (this.exactFiltersOptionsApplied(FilterKey.COVERAGE, [CoveragesFilterOption.OC_AC_MINI])) { // OC + AC Mini
      return acMini && acMini.selectedOption === 'AC_MINI_YES';
    }
  }

  private filterByAdditionalCoverages(offer: Offer): boolean {
    if (!this.filters[FilterKey.ADDITIONAL_COVERAGE].length) {
      return true;
    }

    const nnw = this.getCoverageValue(OfferCoverageCode.NNW, offer);
    if (this.isFilterOptionActive(FilterKey.ADDITIONAL_COVERAGE, AdditionalCoveragesFilterOption.NNW)) {
      if (nnw.selectedOption !== 'NNW_YES') {
        return false;
      }
    }
    const assistance = this.getCoverageValue(OfferCoverageCode.ASSISTANCE, offer);
    if (this.isFilterOptionActive(FilterKey.ADDITIONAL_COVERAGE, AdditionalCoveragesFilterOption.ASSISTANCE)) {
      if (assistance.selectedOption !== 'ASSISTANCE_YES') {
        return false;
      }
    }

    const otherGlass = this.getCoverageValue(OfferCoverageCode.OTHER_GLASS, offer);
    if (this.isFilterOptionActive(FilterKey.ADDITIONAL_COVERAGE, AdditionalCoveragesFilterOption.OTHER_GLASS)) {
      if (otherGlass.selectedOption === 'OTHER_GLASS_NO') {
        return false;
      }
    }
    return true;
  }

  private filterByOwnContribution(offer: Offer): boolean {
    if (!this.filters[FilterKey.OWN_CONTRIBUTION].length) {
      return true;
    }
    const withoutOwnContributionValues = ['AC_EXCESS_NO', 'AC_EXCESS_CANCELLED', 'AC_EXCESS_0', 'AC_EXCESS_ABOLISHED'];
    if (this.isFilterOptionActive(FilterKey.OWN_CONTRIBUTION, OwnContributionFilterOption.WITHOUT_OWN_CONTRIBUTION) &&
        this.isFilterOptionActive(FilterKey.OWN_CONTRIBUTION, OwnContributionFilterOption.WITH_OWN_CONTRIBUTION)) {
      return true;
    }
    const { value, type, selectedOption } = this.getCoverageValue(OfferCoverageCode.AC_EXCESS, offer);
    if (this.isFilterOptionActive(FilterKey.OWN_CONTRIBUTION, OwnContributionFilterOption.WITHOUT_OWN_CONTRIBUTION)) {
      if (selectedOption === 'AC_EXCESS_YES') {
        return type === OfferDestinyOptionType.STRING || (type === OfferDestinyOptionType.NUMBER && value === 0);
      }
      return withoutOwnContributionValues.includes(selectedOption);
    }
    if (this.isFilterOptionActive(FilterKey.OWN_CONTRIBUTION, OwnContributionFilterOption.WITH_OWN_CONTRIBUTION)) {
      if (selectedOption === 'AC_EXCESS_YES') {
        return type === OfferDestinyOptionType.NUMBER || value > 0;
      }
      return !withoutOwnContributionValues.includes(selectedOption);
    }
  }

  private filterByRepairParts(offer: Offer): boolean {
    if (!this.filters[FilterKey.REPAIR_PARTS].length) {
      return true;
    }
    const { value, type, selectedOption } = this.getCoverageValue(OfferCoverageCode.AC_PARTS, offer);
    if (type === OfferDestinyOptionType.BOOLEAN) {
      return value;
    } else {
      if (this.isFilterOptionActive(FilterKey.REPAIR_PARTS, RepairPartsFilterOption.ORIGINAL) && selectedOption === 'AC_PARTS_ORIGINAL') {
        return true;
      }
      if (this.isFilterOptionActive(FilterKey.REPAIR_PARTS, RepairPartsFilterOption.REPLACEMENTS) && selectedOption === 'AC_PARTS_REPLACEMENTS') {
        return true;
      }
    }
  }

  private filterByPartsDepreciation(offer: Offer): boolean {
    if (!this.filters[FilterKey.PARTS_DEPRECIATION].length) {
      return true;
    }
    const { value, type, selectedOption } = this.getCoverageValue(OfferCoverageCode.AC_DEPRECATION, offer);
    if (type === OfferDestinyOptionType.BOOLEAN) {
      return value;
    } else {
      if (this.isFilterOptionActive(FilterKey.PARTS_DEPRECIATION, PartsDepreciationFilterOption.WITH_DEPRECIATION) && selectedOption === 'AC_DEPRECATION_ENABLED') {
        return true;
      }
      if (this.isFilterOptionActive(FilterKey.PARTS_DEPRECIATION, PartsDepreciationFilterOption.WITHOUT_DEPRECIATION) && selectedOption === 'AC_DEPRECATION_ABOLISHED') {
        return true;
      }
    }
  }

  private filterByAssistanceConditions(offer: Offer): boolean {
    if (!this.filters[FilterKey.ASSISTANCE_CONDITIONS].length) {
      return true;
    }

    if (!this.checkAssistanceHappening(offer)) {
      return false;
    }
    if (!this.checkAssistanceTerritorialScope(offer)) {
      return false;
    }
    if (!this.checkAssistanceReplacementCar(offer)) {
      return false;
    }

    return true;
  }

  private filterByNNWValue(offer: Offer): boolean {
    if (!this.filters[FilterKey.NNW_VALUE].length) {
      return true;
    }
    const { value, type } = this.getCoverageValue(OfferCoverageCode.NNW_DRIVER, offer);
    if (type === OfferDestinyOptionType.BOOLEAN) {
      return value;
    } else {
      return this.checkNNWValue(value);
    }
  }

  private filterByPrimaryCoverage(offer: Offer): boolean {
    if (!this.filters[FilterKey.PRIMARY_COVERAGE].length || this.ocOnlySelected) {
      return true;
    }
    if (this.filters[FilterKey.PRIMARY_COVERAGE].includes(OfferPrimaryCoverageCode.AC)) {
      return this.offerCoveragesService.hasOfferPrimaryCoverages(offer, this.filters[FilterKey.PRIMARY_COVERAGE]);
    }
    const hasAc = this.offerCoveragesService.hasOfferPrimaryCoverage(offer, OfferPrimaryCoverageCode.AC) || this.offerCoveragesService.hasOfferPrimaryCoverage(offer, OfferPrimaryCoverageCode.AC_MINI);
    const coverages = _.uniq([
        OfferPrimaryCoverageCode.OC,
        ...this.filters[FilterKey.PRIMARY_COVERAGE]
    ]);
    return this.offerCoveragesService.hasOfferExactPrimaryCoverages(offer, coverages) && !hasAc;
  }

  private filterByNNWRange(offer: Offer, filterState?: any[]): boolean {
    const nnwRangeFilterState = filterState ? filterState : this.filters[FilterKey.NNW_RANGE];
    if (!nnwRangeFilterState.length) {
      return true;
    }
    let { value, type } = this.getCoverageValue(OfferCoverageCode.NNW_DRIVER, offer);
    try {
      if (type === OfferDestinyOptionType.STRING) {
        value = parseInt(value.replace(/ |zł/g, ''));
      }
    } catch (err) {}
    return type === OfferDestinyOptionType.BOOLEAN ? value : this.checkNNWRange(value, filterState);
  }

  private filterByTowingLimit(offer: Offer, filterState?: any[]): boolean {
    const towingLimitFilterState = filterState ? filterState : this.filters[FilterKey.TOWING_LIMIT];
    if (!towingLimitFilterState.length) {
      return true;
    }
    const { value, type, selectedOption } = this.getCoverageValue(OfferCoverageCode.ASSISTANCE_TOWING_ACCIDENT_PL, offer);
    if (type === OfferDestinyOptionType.BOOLEAN) {
      return value;
    }
    if (selectedOption === 'ASSISTANCE_TOWING_ACCIDENT_PL_NO_LIMIT' && this.isFilterOptionActive(FilterKey.TOWING_LIMIT, NextTowingLimitFilterOption.ABOVE_300KM, filterState)) {
      return true;
    } else {
      return this.checkTowingLimit(this.getTowingLimitValue(selectedOption), filterState);
    }
  }

  private getTowingLimitValue(selectedOption: string): number {
    const parts = selectedOption.split('_');
    const towingLimit = parseInt(parts[parts.length - 1]);
    return isNaN(towingLimit) ? 0 : towingLimit;
  }

  private filterByTerritorialScope(offer: Offer, filterState?: any[]): boolean {
    const territorialScopeFilterState = filterState ? filterState : this.filters[FilterKey.TERRITORIAL_SCOPE];
    if (!territorialScopeFilterState.length) {
      return true;
    }
    const { selectedOption } = this.getCoverageValue(OfferCoverageCode.ASSISTANCE_TERRITORIAL_SCOPE, offer);
    if (this.isFilterOptionActive(FilterKey.TERRITORIAL_SCOPE, NextTerritorialScopeFilterOption.POLAND, filterState) && selectedOption === AssistanceTerritorialScopeOption.ASSISTANCE_TERRITORIAL_SCOPE_PL) {
      return true;
    }
    if (this.isFilterOptionActive(FilterKey.TERRITORIAL_SCOPE, NextTerritorialScopeFilterOption.POLAND_EUROPE, filterState) && selectedOption === AssistanceTerritorialScopeOption.ASSISTANCE_TERRITORIAL_SCOPE_EU_PL) {
      return true;
    }
    return false;
  }

  private filterByAccidentReplacementCar(offer: Offer, filterState?: any[]): boolean {
    const accidentReplacementCarFilterState = filterState ? filterState : this.filters[FilterKey.ACCIDENT_REPLACEMENT_CAR];
    if (!accidentReplacementCarFilterState.length) {
      return true;
    }
    const { selectedOption } = this.getCoverageValue(OfferCoverageCode.ASSISTANCE_ACCIDENT_REPLACEMENT_CAR, offer);
    if (selectedOption === AssistanceAccidentReplacementCarOption.ASSISTANCE_ACCIDENT_REPLACEMENT_CAR_NO) {
      return false;
    } else {
      return this.checkAccidentReplacementCar(this.getAccidentReplacementCarValue(selectedOption), filterState);
    }
  }

  private getAccidentReplacementCarValue(selectedOption: string): number {
    const parts = selectedOption.split('_');
    const daysNumber = parseInt(parts[parts.length - 2]);
    return isNaN(daysNumber) ? 0 : daysNumber;
  }

  private filterByInsuranceCompanies(offer: Offer, filterState?: any[]): boolean {
    const insuranceCompanyFilterState = filterState ? filterState : this.filters[FilterKey.INSURANCE_COMPANY];
    if (!insuranceCompanyFilterState.length) {
      return true;
    }
    const insuranceCompanyCode = this.stateService.getBusinessParamFromOffer(offer.id, BusinessParamName.INSURANCE_COMPANY_CODE);
    return insuranceCompanyFilterState.includes(insuranceCompanyCode);
  }

  private updateOffers(): void {
    this.updateFiltersApplied();
    this.filtersChangeEvent.emit();
  }

  private isRadio(filter: OffersFilter): boolean {
    return filter.type === FilterType.RADIO;
  }

  private isCheckbox(filter: OffersFilter): boolean {
    return filter.type === FilterType.CHECKBOX;
  }

  private updateFiltersApplied(): void {
    let allFilters = [];
    Object.keys(this.filters)
        .forEach(filterKey => allFilters = allFilters.concat(this.filters[filterKey]));
    this.filtersApplied = allFilters.length > 0;
  }

  private updateUnconfirmedFiltersApplied(): void {
    let allFilters = [];
    Object.keys(this.unconfirmedFilters).forEach(filterKey => allFilters = allFilters.concat(this.unconfirmedFilters[filterKey]));
    this.unconfirmedFiltersApplied = allFilters.length > 0;
  }

  private getCoverageValue(code: OfferCoverageCode, offer: Offer): any {
    const offerCoverage = offer.coverages.find(coverage => coverage.code === code);
    if (offerCoverage) {
      const codeToFind = code === OfferCoverageCode.AC_MINI ? 'PRIMARY_BOX' : 'COMPARE';
      const offerDestiny = offerCoverage.destinies.find(destiny => destiny.code === codeToFind);
      const values = offerDestiny.options.find(option => option.code === offerDestiny.selectedOption);
      return {
        value: this.lang.getTranslationForKey(values.values, 'value'),
        type: values.type,
        selectedOption: offerDestiny.selectedOption
      };
    }
    return null;
  }

  private checkNNWValue(value: number): boolean {
    if (value <= 20000 && this.filters[FilterKey.NNW_VALUE].includes(NNWValueFilterOption.UNDER_20)) {
      return true;
    }
    if (value >= 20000 && value <= 40000 && this.filters[FilterKey.NNW_VALUE].includes(NNWValueFilterOption.FROM_20_TO_40)) {
      return true;
    }
    if (value > 40000 && this.filters[FilterKey.NNW_VALUE].includes(NNWValueFilterOption.ABOVE_40)) {
      return true;
    }

    return false;
  }

  private checkAssistanceHappening(offer: Offer): boolean {
    const { value, type, selectedOption } = this.getCoverageValue(OfferCoverageCode.ASSISTANCE_HAPPENING, offer);
    if (type === OfferDestinyOptionType.BOOLEAN) {
      return value;
    } else {
      if (this.isFilterOptionActive(FilterKey.ASSISTANCE_CONDITIONS, AssistanceConditionsFilterOption.ACCIDENT)) {
        const hasAccident = ['ASSISTANCE_HAPPENING_ACCIDENT', 'ASSISTANCE_HAPPENING_BREAKDOWN_AND_ACCIDENT', 'ASSISTANCE_HAPPENING_BREAKDOWN_AND_ACCIDENT_AND_THEFT'].includes(selectedOption);
        if (!hasAccident) {
          return false;
        }
      }
      if (this.isFilterOptionActive(FilterKey.ASSISTANCE_CONDITIONS, AssistanceConditionsFilterOption.BREAKDOWN)) {
        const hasBreakdown = ['ASSISTANCE_HAPPENING_BREAKDOWN_AND_ACCIDENT', 'ASSISTANCE_HAPPENING_BREAKDOWN_AND_ACCIDENT_AND_THEFT'].includes(selectedOption);
        if (!hasBreakdown) {
          return false;
        }
      }
    }
    return true;
  }

  private checkAssistanceTerritorialScope(offer: Offer): boolean {
    const { value, type, selectedOption } = this.getCoverageValue(OfferCoverageCode.ASSISTANCE_TERRITORIAL_SCOPE, offer);
    if (type === OfferDestinyOptionType.BOOLEAN) {
      return value;
    } else {
      if (this.isFilterOptionActive(FilterKey.ASSISTANCE_CONDITIONS, AssistanceConditionsFilterOption.POLAND)) {
        const hasPoland = [AssistanceTerritorialScopeOption.ASSISTANCE_TERRITORIAL_SCOPE_PL, AssistanceTerritorialScopeOption.ASSISTANCE_TERRITORIAL_SCOPE_EU_PL].includes(selectedOption);
        if (!hasPoland) {
          return false;
        }
      }
      if (this.isFilterOptionActive(FilterKey.ASSISTANCE_CONDITIONS, AssistanceConditionsFilterOption.EUROPE)) {
        const hasEurope = selectedOption === AssistanceTerritorialScopeOption.ASSISTANCE_TERRITORIAL_SCOPE_EU_PL;
        if (!hasEurope) {
          return false;
        }
      }
    }
    return true;
  }

  private checkAssistanceReplacementCar(offer: Offer): boolean {
    const accidentCar = this.getCoverageValue(OfferCoverageCode.ASSISTANCE_ACCIDENT_REPLACEMENT_CAR, offer);
    const breakdownCar = this.getCoverageValue(OfferCoverageCode.ASSISTANCE_BREAKDOWN_REPLACEMENT_CAR, offer);

    if (this.isFilterOptionActive(FilterKey.ASSISTANCE_CONDITIONS, AssistanceConditionsFilterOption.ACCIDENT_REPLACEMENT_CAR)) {
      if (accidentCar.type === OfferDestinyOptionType.BOOLEAN && !accidentCar.value) {
        return false;
      }
    }

    if (this.isFilterOptionActive(FilterKey.ASSISTANCE_CONDITIONS, AssistanceConditionsFilterOption.BREAKDOWN_REPLACEMENT_CAR)) {
      if (breakdownCar.type === OfferDestinyOptionType.BOOLEAN && !breakdownCar.value) {
        return false;
      }
    }

    return true;
  }

  /** next filter */
  private checkNNWRange(value: number, filterState?: any[]): boolean {
    if (value <= 10000 && this.isFilterOptionActive(FilterKey.NNW_RANGE, NextNNWValueFilterOption.UNDER_10, filterState)) {
      return true;
    }
    if (value > 10000 && this.isFilterOptionActive(FilterKey.NNW_RANGE, NextNNWValueFilterOption.ABOVE_10, filterState)) {
      return true;
    }

    return false;
  }

  private checkTowingLimit(value: number, filterState?: any[]): boolean {
    if (value > 0 && value < 150 && this.isFilterOptionActive(FilterKey.TOWING_LIMIT, NextTowingLimitFilterOption.UNDER_150KM, filterState)) {
      return true;
    }
    if (value >= 150 && value <= 300 && this.isFilterOptionActive(FilterKey.TOWING_LIMIT, NextTowingLimitFilterOption.FROM_150KM_TO_300KM, filterState)) {
      return true;
    }
    if (value > 300 && this.isFilterOptionActive(FilterKey.TOWING_LIMIT, NextTowingLimitFilterOption.ABOVE_300KM, filterState)) {
      return true;
    }
    return false;
  }

  private checkAccidentReplacementCar(value: number, filterState?: any[]): boolean {
    if (value > 0 && value < 3 && this.isFilterOptionActive(FilterKey.ACCIDENT_REPLACEMENT_CAR, NextAccidentReplacementCarFilterOption.UNDER_3_DAYS, filterState)) {
      return true;
    }
    if (value >= 3 && value <= 4 && this.isFilterOptionActive(FilterKey.ACCIDENT_REPLACEMENT_CAR, NextAccidentReplacementCarFilterOption.FROM_3_TO_4_DAYS, filterState)) {
      return true;
    }
    if (value > 4 && this.isFilterOptionActive(FilterKey.ACCIDENT_REPLACEMENT_CAR, NextAccidentReplacementCarFilterOption.ABOVE_4_DAYS, filterState)) {
      return true;
    }
    return false;
  }

  private isFilterOptionActive(filterKey: FilterKey, optionKey: any, filterState?: any[]): boolean {
    if (isNotNullOrUndefined(filterState)) {
      return filterState.includes(optionKey);
    }
    return this.filters[filterKey].includes(optionKey);
  }

  private exactFiltersOptionsApplied(filterKey: FilterKey, optionsKeys: any[]): boolean {
    return _.isEqual(_.sortBy(optionsKeys), _.sortBy(this.filters[filterKey]));
  }

  private createClearFiltersState(): FiltersState {
    const filtersState = {};
    Object.values(FilterKey).forEach(filterKey => filtersState[filterKey] = []);
    return filtersState;
  }

  private checkAcConcernedFilters(): void {
    let allAcConcernedFiltersOptionsAmount = 0;
    this.acConcernedFilters.forEach(filterKey => allAcConcernedFiltersOptionsAmount += this.filtersState[filterKey].length);
    if (!allAcConcernedFiltersOptionsAmount) {
      this.filterMessageEvent.emit({ targets: [ FilterKey.COVERAGE ], message: FilterMessage.REMOVE_OC_AC });
    }
  }

  private getPrimaryCoverageFilterRelatedFilters(coverageCode: string): string[] {
    const config = this.primaryCoverageFiltersConfig.find(item => item.primaryCoverageCode === coverageCode);
    return config ? config.relatedDetailCoverageFilterKeys : [];
  }
}

export interface PrimaryCoverageFilterConfig {
  primaryCoverageCode: OfferPrimaryCoverageCode;
  relatedDetailCoverageFilterKeys: string[];
}

export interface DetailCoverageFilterClickEvent {
  filterKey: FilterKey;
  optionKey: string;
  checked: boolean;
}
